import { AbstractControl, FormControl, FormGroup, ValidationErrors } from '@angular/forms';

export const INGREDIENTS_MAX_LENGTH = 5000;
export const INGREDIENTS_SOFT_WARN_LENGTH = 4500;

export interface AllergenOption {
  value: string;
  label: string;
}

export const EU_ALLERGEN_OPTIONS: AllergenOption[] = [
  { value: 'gluten', label: 'Gluten' },
  { value: 'crustaceans', label: 'Crustaceans' },
  { value: 'eggs', label: 'Eggs' },
  { value: 'fish', label: 'Fish' },
  { value: 'peanuts', label: 'Peanuts' },
  { value: 'soybeans', label: 'Soybeans' },
  { value: 'milk', label: 'Milk' },
  { value: 'nuts', label: 'Tree Nuts' },
  { value: 'celery', label: 'Celery' },
  { value: 'mustard', label: 'Mustard' },
  { value: 'sesame', label: 'Sesame' },
  { value: 'sulphites', label: 'Sulphites' },
  { value: 'lupin', label: 'Lupin' },
  { value: 'molluscs', label: 'Molluscs' }
];

export interface NutritionFieldDef {
  key: string;
  label: string;
  unit: string;
}

export const NUTRITION_FIELDS: NutritionFieldDef[] = [
  { key: 'energyKJ', label: 'Energy', unit: 'kJ' },
  { key: 'energyKcal', label: 'Energy', unit: 'kcal' },
  { key: 'fat', label: 'Fat', unit: 'g' },
  { key: 'saturatedFat', label: 'of which Saturated', unit: 'g' },
  { key: 'carbohydrates', label: 'Carbohydrates', unit: 'g' },
  { key: 'sugars', label: 'of which Sugars', unit: 'g' },
  { key: 'fiber', label: 'Fiber', unit: 'g' },
  { key: 'protein', label: 'Protein', unit: 'g' },
  { key: 'salt', label: 'Salt', unit: 'g' }
];

export function nonNegativeNumberValidator(control: AbstractControl): ValidationErrors | null {
  const val = control.value;
  if (val === null || val === undefined || val === '') {
    return null;
  }
  const num = Number(val);
  if (Number.isNaN(num) || num < 0) {
    return { nonNegative: { message: 'Value must be 0 or greater' } };
  }
  return null;
}

export function createNutritionFormGroup(): FormGroup {
  const controls: Record<string, FormControl> = {};
  for (const field of NUTRITION_FIELDS) {
    controls[field.key] = new FormControl(null, [nonNegativeNumberValidator]);
  }
  return new FormGroup(controls);
}

export function parseAllergenArray(raw: any): string[] {
  if (!raw) {
    return [];
  }
  if (Array.isArray(raw)) {
    return raw.filter(Boolean);
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch {
      return [];
    }
  }
  return [];
}

export function appendIngredientsNutritionFormData(
  fd: FormData,
  formValue: any,
  options: { includeAllergens: boolean; includeNutrition: boolean }
): void {
  if (options.includeAllergens) {
    fd.append('ingredients', formValue.ingredients || '');
    fd.append('allergens', JSON.stringify(formValue.allergens || []));
    fd.append('allergenTraces', JSON.stringify(formValue.allergenTraces || []));
  }

  if (options.includeNutrition) {
    const nutrition = formValue.nutrition || {};
    for (const field of NUTRITION_FIELDS) {
      const val = nutrition[field.key];
      if (val !== null && val !== undefined && val !== '') {
        fd.append(`nutrition[${field.key}]`, String(val));
      }
    }
  }
}

export function patchIngredientsNutrition(form: FormGroup, product: any): void {
  const nutritionPatch: Record<string, any> = {};
  const nutrition = product?.nutrition || {};
  for (const field of NUTRITION_FIELDS) {
    nutritionPatch[field.key] = nutrition[field.key] ?? null;
  }

  form.patchValue({
    ingredients: product?.ingredients || '',
    allergens: parseAllergenArray(product?.allergens),
    allergenTraces: parseAllergenArray(product?.allergenTraces),
    nutrition: nutritionPatch
  });
}

export function toggleAllergenSelection(current: string[], value: string, checked: boolean): string[] {
  const set = new Set(current || []);
  if (checked) {
    set.add(value);
  } else {
    set.delete(value);
  }
  return Array.from(set);
}

export function isAllergenSelected(selected: string[] | null | undefined, value: string): boolean {
  return (selected || []).includes(value);
}
